import { useCallback, useEffect, useRef, useState } from "react";

/** Browser Web Speech API helpers (no external keys) — speech-to-text + read-aloud. */

const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

export const speechInputSupported = () => !!SR;
export const speechOutputSupported = () => typeof window !== "undefined" && !!window.speechSynthesis;

/**
 * Speech-to-text. `onTranscript(text)` fires with base + interim/final transcript while listening;
 * `onEnd(finalText)` fires once when recognition stops (user toggled off, silence, or error).
 */
export function useSpeechInput({ lang = "en-AU", onTranscript, onEnd } = {}) {
  const recRef = useRef(null);
  const baseRef = useRef("");
  const lastRef = useRef("");
  const cbRef = useRef({ onTranscript, onEnd });
  cbRef.current = { onTranscript, onEnd };
  const [listening, setListening] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const stop = useCallback(() => {
    try {
      recRef.current?.stop();
    } catch {
      /* already stopped */
    }
  }, []);

  const start = useCallback(
    (base = "") => {
      if (!SR) return false;
      try {
        recRef.current?.abort();
      } catch {
        /* ignore */
      }
      const rec = new SR();
      rec.lang = lang;
      rec.continuous = true;
      rec.interimResults = true;
      baseRef.current = base.trim() ? `${base.trim()} ` : "";
      lastRef.current = "";
      rec.onresult = (e) => {
        let fin = "";
        let interim = "";
        for (let i = 0; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) fin += t;
          else interim += t;
        }
        const text = (baseRef.current + fin + interim).trimStart();
        lastRef.current = text;
        cbRef.current.onTranscript?.(text);
      };
      rec.onend = () => {
        setListening(false);
        recRef.current = null;
        cbRef.current.onEnd?.(lastRef.current);
      };
      rec.onerror = (e) => {
        if (e?.error === "not-allowed" || e?.error === "service-not-allowed") setBlocked(true);
        setListening(false);
      };
      recRef.current = rec;
      try {
        rec.start();
        setListening(true);
        return true;
      } catch {
        setListening(false);
        return false;
      }
    },
    [lang]
  );

  useEffect(
    () => () => {
      try {
        recRef.current?.abort();
      } catch {
        /* ignore */
      }
    },
    []
  );

  return { supported: !!SR, listening, blocked, start, stop };
}

/** Read-aloud via speechSynthesis; tracks which message id is currently being spoken. */
export function useReadAloud({ lang = "en-AU", rate = 1.02 } = {}) {
  const [speakingId, setSpeakingId] = useState("");

  const stop = useCallback(() => {
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* not supported */
    }
    setSpeakingId("");
  }, []);

  const speak = useCallback(
    (id, text) => {
      const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
      if (!synth) return false;
      synth.cancel();
      const body = String(text || "").trim();
      if (!body) {
        setSpeakingId("");
        return true;
      }
      const u = new SpeechSynthesisUtterance(body.slice(0, 4000));
      u.lang = lang;
      const voices = synth.getVoices() || [];
      u.voice = voices.find((v) => v.lang === lang) || voices.find((v) => (v.lang || "").startsWith("en")) || null;
      u.rate = rate;
      const clear = () => setSpeakingId((cur) => (cur === id ? "" : cur));
      u.onend = clear;
      u.onerror = clear;
      setSpeakingId(id);
      synth.speak(u);
      return true;
    },
    [lang, rate]
  );

  const toggle = useCallback((id, text) => (speakingId === id ? stop() : speak(id, text)), [speakingId, speak, stop]);

  useEffect(() => () => stop(), [stop]);

  return { supported: speechOutputSupported(), speakingId, speak, stop, toggle };
}
